/**
 * My Profile Page — schlanker Orchestrator.
 * Delegiert an useMyProfile Hook, Tab-Components und Dialog-Components.
 */
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { User, AlertCircle, Bell, KeyRound, CalendarDays, ShieldCheck } from 'lucide-react';

import { useMyProfile } from './profile/useMyProfile';
import {
  PersonalDataTab, EmploymentDataTab, QualificationsTab,
  RequestsTab, NotificationsTab, ITAccessTab, ActiveSessionsCard,
} from './profile/ProfileTabs';
import { MyCalendarTab } from './profile/MyCalendarTab';
import { ChangeRequestDialog, HistoryDialog } from './profile/ProfileDialogs';
import { PasskeySection } from './profile/PasskeySection';
import { ReferralCard } from './profile/ReferralCard';

const MyProfile = () => {
  const [searchParams] = useSearchParams();
  const p = useMyProfile();

  if (p.loading) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6" data-testid="my-profile-loading">
          <Skeleton className="h-8 w-48" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Skeleton className="h-64" />
            <Skeleton className="h-64" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!p.employee) {
    return (
      <DashboardLayout>
        <div className="p-6 space-y-6" data-testid="my-profile-no-employee">
          <ReferralCard />
          <Card>
            <CardContent className="p-12 text-center">
              <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-xl font-semibold mb-2">Kein Mitarbeiterprofil gefunden</h2>
              <p className="text-muted-foreground">
                Ihr Benutzerkonto ist noch nicht mit einem Mitarbeiterprofil verknüpft.
                Bitte wenden Sie sich an Ihren Administrator.
              </p>
            </CardContent>
          </Card>
          {/* Sicherheits-Einstellungen sind kontounabhängig nutzbar */}
          <PasskeySection />
          <ActiveSessionsCard />
        </div>
      </DashboardLayout>
    );
  }

  const pendingCount = p.myRequests.filter((r) => r.status === 'pending').length;

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 pb-20 sm:pb-6 space-y-4 sm:space-y-6" data-testid="my-profile-page">
        {/* Header */}
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <User className="h-6 w-6 sm:h-8 sm:w-8 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold truncate">{p.employee.first_name} {p.employee.last_name}</h1>
            <div className="flex items-center gap-2 text-muted-foreground text-sm flex-wrap">
              <Badge variant="outline">{p.employee.employee_number}</Badge>
              {p.employee.position && <span className="truncate">{p.employee.position}</span>}
            </div>
          </div>
        </div>

        <Tabs defaultValue={searchParams.get('tab') || 'employment'} className="space-y-4">
          <div className="-mx-4 sm:mx-0 overflow-x-auto scrollbar-hide">
            <TabsList className="inline-flex w-max sm:w-auto sm:flex-wrap px-4 sm:px-0">
              <TabsTrigger value="employment" data-testid="tab-employment" className="whitespace-nowrap">Beschäftigung</TabsTrigger>
              <TabsTrigger value="my-calendar" data-testid="tab-my-calendar" className="whitespace-nowrap">
                <CalendarDays className="h-4 w-4 mr-1" />
                Mein Kalender
              </TabsTrigger>
              <TabsTrigger value="personal" data-testid="tab-personal" className="whitespace-nowrap">Persönliche Daten</TabsTrigger>
              <TabsTrigger value="qualifications" data-testid="tab-qualifications" className="whitespace-nowrap">Weiterbildung &amp; Qualifikation</TabsTrigger>
              <TabsTrigger value="it-access" data-testid="tab-it-access" className="whitespace-nowrap">
                <KeyRound className="h-4 w-4 mr-1" />
                IT-Zugänge
              </TabsTrigger>
              <TabsTrigger value="requests" data-testid="tab-requests" className="relative whitespace-nowrap">
                Meine Anträge
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-yellow-500 text-white text-xs rounded-full flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="benachrichtigungen" data-testid="tab-notifications" className="whitespace-nowrap">
                <Bell className="h-4 w-4 mr-1" />
                Benachrichtigungen
              </TabsTrigger>
              <TabsTrigger value="security" data-testid="tab-security" className="whitespace-nowrap">
                <ShieldCheck className="h-4 w-4 mr-1" />
                Sicherheit
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="personal">
            <div className="space-y-4 sm:space-y-6">
              <PersonalDataTab employee={p.employee} openChangeDialog={p.openChangeDialog} />
              <ReferralCard />
            </div>
          </TabsContent>

          <TabsContent value="employment">
            <EmploymentDataTab
              employee={p.employee}
              openChangeDialog={p.openChangeDialog}
            />
          </TabsContent>

          <TabsContent value="my-calendar">
            <MyCalendarTab />
          </TabsContent>

          <TabsContent value="qualifications">
            <QualificationsTab employee={p.employee} />
          </TabsContent>

          <TabsContent value="it-access">
            <ITAccessTab />
          </TabsContent>

          <TabsContent value="requests">
            <RequestsTab
              myRequests={p.myRequests}
              loadingRequests={p.loadingRequests}
              openHistoryDialog={p.openHistoryDialog}
              handleDeleteRequest={p.handleDeleteRequest}
            />
          </TabsContent>

          <TabsContent value="benachrichtigungen">
            <NotificationsTab
              loadingNotifSettings={p.loadingNotifSettings}
              notificationSettings={p.notificationSettings}
              savingNotifSettings={p.savingNotifSettings}
              updateNotificationSetting={p.updateNotificationSetting}
            />
          </TabsContent>

          <TabsContent value="security">
            <PasskeySection />
          </TabsContent>
        </Tabs>

        <ChangeRequestDialog
          open={p.changeDialogOpen}
          onOpenChange={p.setChangeDialogOpen}
          selectedCategory={p.selectedCategory}
          setSelectedCategory={p.setSelectedCategory}
          fieldValues={p.fieldValues}
          setFieldValues={p.setFieldValues}
          reason={p.reason}
          setReason={p.setReason}
          employee={p.employee}
          submitting={p.submitting}
          onSubmit={p.handleSubmitChangeRequest}
        />

        <HistoryDialog
          open={p.historyDialogOpen}
          onOpenChange={p.setHistoryDialogOpen}
          selectedRequest={p.selectedRequest}
        />
      </div>
    </DashboardLayout>
  );
};

export default MyProfile;
